import * as z from 'zod';

import * as Types from './graphql';

type Properties<T> = Required<{
  [K in keyof T]: z.ZodType<T[K]>;
}>;

type definedNonNullAny = {};

export const isDefinedNonNullAny = (v: any): v is definedNonNullAny => v !== undefined && v !== null;

export const definedNonNullAnySchema = z.any().refine((v) => isDefinedNonNullAny(v));

export const ModelSortDirectionSchema = z.enum(Types.ModelSortDirection);

export function CalendarWidgetInfoInputSchema(): z.ZodObject<Properties<Types.CalendarWidgetInfoInput>> {
  return z.object({
    endDate: z.string().nullish(),
    href: z.string().nullish(),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
    surface: z.string().nullish(),
    tournamentName: z.string().nullish(),
    tournamentPlace: z.string().nullish(),
    tournamentType: z.string().nullish(),
  });
}

export function CreateCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.CreateCalendarWidgetInput>> {
  return z.object({
    circuitCalendarLink: z.string().nullish(),
    filterList: z.array(z.string().nullable()).nullish(),
    tourId: z.string(),
    tournamentFilter: z.string(),
    tournaments: z.array(z.lazy(() => CalendarWidgetInfoInputSchema().nullable())).nullish(),
  });
}

export function CreateCompetitionInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    competitionJson: z.any().nullish(),
    competitionName: z.string().nullish(),
    editorialDataId: z.string().nullish(),
    endDate: z.string().nullish(),
    startDate: z.string().nullish(),
    tourCalendarId: z.string(),
    tourId: z.string(),
    type: z.string().nullish(),
  });
}

export function CreateEditorialDataInputSchema(): z.ZodObject<Properties<Types.CreateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any(),
    seasonId: z.string(),
  });
}

export function CreateEditorialDataV2InputSchema(): z.ZodObject<Properties<Types.CreateEditorialDataV2Input>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any(),
    tourCalendarId: z.string(),
  });
}

export function CreateLiveScoreWidgetInputSchema(): z.ZodObject<Properties<Types.CreateLiveScoreWidgetInput>> {
  return z.object({
    ctaUrls: z.lazy(() => LiveScoreWidgetCtaUrlInputSchema().nullish()),
    id: z.string().nullish(),
    liveTournaments: z.array(z.lazy(() => LiveScoreWidgetTournamentInputSchema().nullable())).nullish(),
  });
}

export function CreateMatchCardWidgetInputSchema(): z.ZodObject<Properties<Types.CreateMatchCardWidgetInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    competitionId: z.string().nullish(),
    date: z.string().nullish(),
    firstPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    matchId: z.string(),
    resultType: z.string().nullish(),
    roundName: z.string().nullish(),
    secondPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
    tournamentId: z.string().nullish(),
  });
}

export function CreateMatchInputSchema(): z.ZodObject<Properties<Types.CreateMatchInput>> {
  return z.object({
    competitionId: z.string(),
    date: z.number().nullish(),
    matchId: z.string(),
    matchLiveJson: z.any().nullish(),
    matchScheduleJson: z.any().nullish(),
    playerA1Id: z.string().nullish(),
    playerA2Id: z.string().nullish(),
    playerAseed: z.string().nullish(),
    playerB1Id: z.string().nullish(),
    playerB2Id: z.string().nullish(),
    playerBseed: z.string().nullish(),
    roundName: z.string().nullish(),
    status: z.string().nullish(),
    teamAId: z.string().nullish(),
    teamBId: z.string().nullish(),
    tourCalendarId: z.string(),
    tourId: z.string(),
    tournamentId: z.string(),
  });
}

export function CreatePlayerInputSchema(): z.ZodObject<Properties<Types.CreatePlayerInput>> {
  return z.object({
    nationalityId: z.string().nullish(),
    playerId: z.string(),
    playerJson: z.any().nullish(),
    playerName: z.string().nullish(),
  });
}

export function CreateRankingInputSchema(): z.ZodObject<Properties<Types.CreateRankingInput>> {
  return z.object({
    playerId: z.string(),
    rank: z.number(),
    rankingJson: z.any().nullish(),
    tourId: z.string(),
    type: z.string(),
  });
}

export function CreateRankingWidgetInputSchema(): z.ZodObject<Properties<Types.CreateRankingWidgetInput>> {
  return z.object({
    rankings: z.array(z.lazy(() => RankingWidgetInfoInputSchema().nullable())).nullish(),
    tourId: z.string(),
    type: z.string(),
  });
}

export function CreateResultsCategoriesWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateResultsCategoriesWidgetInput>
> {
  return z.object({
    adv: z.lazy(() => ResultsCategoriesWidgetAdvInputSchema().nullish()),
    categories: z.array(z.lazy(() => ResultsCategoriesWidgetInfoInputSchema().nullable())).nullish(),
    competitionId: z.string(),
    type: z.string(),
  });
}

export function CreateResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateResultsContainerWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    ssi: z.array(z.string().nullable()).nullish(),
    type: z.string(),
  });
}

export function CreateResultsWidgetInputSchema(): z.ZodObject<Properties<Types.CreateResultsWidgetInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    date: z.string().nullish(),
    day: z.string(),
    matches: z.array(z.lazy(() => ResultsWidgetMatchInputSchema().nullable())).nullish(),
    tournamentId: z.string(),
  });
}

export function CreateTourCalendarInputSchema(): z.ZodObject<Properties<Types.CreateTourCalendarInput>> {
  return z.object({
    active: z.boolean().nullish(),
    endDate: z.string().nullish(),
    startDate: z.string().nullish(),
    tourCalendarId: z.string(),
    tourId: z.string(),
    tourName: z.string().nullish(),
    tourYear: z.string().nullish(),
  });
}

export function CreateTournamentDataWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateTournamentDataWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    data: z.any().nullish(),
    messageType: z.string(),
    type: z.string(),
  });
}

export function CreateTournamentInputSchema(): z.ZodObject<Properties<Types.CreateTournamentInput>> {
  return z.object({
    competitionId: z.string(),
    tournamentId: z.string(),
    tournamentJson: z.any().nullish(),
    tournamentName: z.string().nullish(),
  });
}

export function DeleteCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteCalendarWidgetInput>> {
  return z.object({
    tourId: z.string(),
    tournamentFilter: z.string(),
  });
}

export function DeleteCompetitionInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    tourCalendarId: z.string(),
  });
}

export function DeleteEditorialDataInputSchema(): z.ZodObject<Properties<Types.DeleteEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteEditorialDataV2InputSchema(): z.ZodObject<Properties<Types.DeleteEditorialDataV2Input>> {
  return z.object({
    editorialDataId: z.string(),
    tourCalendarId: z.string(),
  });
}

export function DeleteLiveScoreWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteLiveScoreWidgetInput>> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteMatchCardWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteMatchCardWidgetInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteMatchInputSchema(): z.ZodObject<Properties<Types.DeleteMatchInput>> {
  return z.object({
    matchId: z.string(),
    tournamentId: z.string(),
  });
}

export function DeletePlayerInputSchema(): z.ZodObject<Properties<Types.DeletePlayerInput>> {
  return z.object({
    playerId: z.string(),
  });
}

export function DeleteRankingInputSchema(): z.ZodObject<Properties<Types.DeleteRankingInput>> {
  return z.object({
    rank: z.number(),
    tourId: z.string(),
    type: z.string(),
  });
}

export function DeleteRankingWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteRankingWidgetInput>> {
  return z.object({
    tourId: z.string(),
    type: z.string(),
  });
}

export function DeleteResultsCategoriesWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteResultsCategoriesWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    type: z.string(),
  });
}

export function DeleteResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteResultsContainerWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    type: z.string(),
  });
}

export function DeleteResultsWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteResultsWidgetInput>> {
  return z.object({
    day: z.string(),
    tournamentId: z.string(),
  });
}

export function DeleteTourCalendarInputSchema(): z.ZodObject<Properties<Types.DeleteTourCalendarInput>> {
  return z.object({
    tourCalendarId: z.string(),
    tourId: z.string(),
  });
}

export function DeleteTournamentDataWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteTournamentDataWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    type: z.string(),
  });
}

export function DeleteTournamentInputSchema(): z.ZodObject<Properties<Types.DeleteTournamentInput>> {
  return z.object({
    competitionId: z.string(),
    tournamentId: z.string(),
  });
}

export function LiveScoreWidgetCtaUrlInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetCtaUrlInput>> {
  return z.object({
    atpCalendarUrl: z.string().nullish(),
    atpRankingUrl: z.string().nullish(),
    wtaCalendarUrl: z.string().nullish(),
    wtaRankingUrl: z.string().nullish(),
  });
}

export function LiveScoreWidgetMatchInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetMatchInput>> {
  return z.object({
    firstPosition: z.lazy(() => LiveScoreWidgetPositionInputSchema().nullish()),
    resultType: z.string().nullish(),
    roundName: z.string().nullish(),
    secondPosition: z.lazy(() => LiveScoreWidgetPositionInputSchema().nullish()),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function LiveScoreWidgetPlayerInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetPlayerInput>> {
  return z.object({
    name: z.string().nullish(),
    nationality: z.string().nullish(),
    seed: z.string().nullish(),
  });
}

export function LiveScoreWidgetPositionInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetPositionInput>> {
  return z.object({
    gamePoints: z.string().nullish(),
    isServing: z.boolean().nullish(),
    players: z.array(z.lazy(() => LiveScoreWidgetPlayerInputSchema().nullable())).nullish(),
    score: z.array(z.lazy(() => LiveScoreWidgetScoreInputSchema().nullable())).nullish(),
  });
}

export function LiveScoreWidgetScoreInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetScoreInput>> {
  return z.object({
    isWinner: z.boolean().nullish(),
    set: z.string().nullish(),
    tie: z.string().nullish(),
  });
}

export function LiveScoreWidgetTournamentInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetTournamentInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    competitionId: z.string().nullish(),
    matches: z.array(z.lazy(() => LiveScoreWidgetMatchInputSchema().nullable())).nullish(),
    resultsUrl: z.string().nullish(),
    tourName: z.string().nullish(),
    tourYear: z.string().nullish(),
    tournamentName: z.string().nullish(),
    tournamentType: z.string().nullish(),
  });
}

export function ModelBooleanFilterInputSchema(): z.ZodObject<Properties<Types.ModelBooleanFilterInput>> {
  return z.object({
    eq: z.boolean().nullish(),
    ne: z.boolean().nullish(),
  });
}

export function ModelCalendarWidgetFilterInputSchema(): z.ZodObject<Properties<Types.ModelCalendarWidgetFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelCalendarWidgetFilterInputSchema().nullable())).nullish(),
    circuitCalendarLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    filterList: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCalendarWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCalendarWidgetFilterInputSchema().nullable())).nullish(),
    tourId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentFilter: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionFilterInputSchema(): z.ZodObject<Properties<Types.ModelCompetitionFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    endDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionFilterInputSchema().nullable())).nullish(),
    startDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tourCalendarId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tourId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelEditorialDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelEditorialDataFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    editorialJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelEditorialDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelEditorialDataV2FilterInputSchema(): z.ZodObject<
  Properties<Types.ModelEditorialDataV2FilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelEditorialDataV2FilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    editorialJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelEditorialDataV2FilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelEditorialDataV2FilterInputSchema().nullable())).nullish(),
    tourCalendarId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelFloatFilterInputSchema(): z.ZodObject<Properties<Types.ModelFloatFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelIDFilterInputSchema(): z.ZodObject<Properties<Types.ModelIDFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelIDKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIDKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function ModelIntFilterInputSchema(): z.ZodObject<Properties<Types.ModelIntFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelIntKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIntKeyConditionInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
  });
}

export function ModelLiveScoreWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLiveScoreWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLiveScoreWidgetFilterInputSchema().nullable())).nullish(),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLiveScoreWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLiveScoreWidgetFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelMatchCardWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelMatchCardWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchCardWidgetFilterInputSchema().nullable())).nullish(),
    categoryName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchCardWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchCardWidgetFilterInputSchema().nullable())).nullish(),
    resultType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    roundName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    startDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelMatchFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    date: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchLiveJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchScheduleJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchFilterInputSchema().nullable())).nullish(),
    playerA1Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerA2Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerAseed: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    playerB1Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerB2Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerBseed: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    roundName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    teamAId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    teamBId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tourCalendarId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tourId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelPlayerFilterInputSchema(): z.ZodObject<Properties<Types.ModelPlayerFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelPlayerFilterInputSchema().nullable())).nullish(),
    nationalityId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelPlayerFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPlayerFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    playerName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelRankingFilterInputSchema(): z.ZodObject<Properties<Types.ModelRankingFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelRankingFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelRankingFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    rank: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    rankingJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tourId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelRankingPrimaryCompositeKeyConditionInputSchema(): z.ZodObject<
  Properties<Types.ModelRankingPrimaryCompositeKeyConditionInput>
> {
  return z.object({
    beginsWith: z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullish()),
    between: z.array(z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullable())).nullish(),
    eq: z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullish()),
    ge: z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullish()),
    gt: z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullish()),
    le: z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullish()),
    lt: z.lazy(() => ModelRankingPrimaryCompositeKeyInputSchema().nullish()),
  });
}

export function ModelRankingPrimaryCompositeKeyInputSchema(): z.ZodObject<
  Properties<Types.ModelRankingPrimaryCompositeKeyInput>
> {
  return z.object({
    rank: z.number().nullish(),
    type: z.string().nullish(),
  });
}

export function ModelRankingWidgetFilterInputSchema(): z.ZodObject<Properties<Types.ModelRankingWidgetFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelRankingWidgetFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelRankingWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelRankingWidgetFilterInputSchema().nullable())).nullish(),
    tourId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelResultsCategoriesWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelResultsCategoriesWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelResultsCategoriesWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelResultsCategoriesWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelResultsCategoriesWidgetFilterInputSchema().nullable())).nullish(),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelResultsContainerWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelResultsContainerWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelResultsContainerWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    ssi: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelResultsWidgetFilterInputSchema(): z.ZodObject<Properties<Types.ModelResultsWidgetFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelResultsWidgetFilterInputSchema().nullable())).nullish(),
    categoryName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    day: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelResultsWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelResultsWidgetFilterInputSchema().nullable())).nullish(),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelStringFilterInputSchema(): z.ZodObject<Properties<Types.ModelStringFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelStringKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelStringKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function ModelTourCalendarFilterInputSchema(): z.ZodObject<Properties<Types.ModelTourCalendarFilterInput>> {
  return z.object({
    active: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    and: z.array(z.lazy(() => ModelTourCalendarFilterInputSchema().nullable())).nullish(),
    endDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTourCalendarFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTourCalendarFilterInputSchema().nullable())).nullish(),
    startDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tourCalendarId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tourId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tourName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tourYear: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelTournamentDataWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelTournamentDataWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelTournamentDataWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    data: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    messageType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTournamentDataWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTournamentDataWidgetFilterInputSchema().nullable())).nullish(),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelTournamentFilterInputSchema(): z.ZodObject<Properties<Types.ModelTournamentFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelTournamentFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTournamentFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTournamentFilterInputSchema().nullable())).nullish(),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tournamentName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function RankingWidgetInfoInputSchema(): z.ZodObject<Properties<Types.RankingWidgetInfoInput>> {
  return z.object({
    nationality: z.string().nullish(),
    playerName: z.string().nullish(),
    points: z.string().nullish(),
    rank: z.number().nullish(),
    trend: z.string().nullish(),
  });
}

export function ResultWidgetPlayerInputSchema(): z.ZodObject<Properties<Types.ResultWidgetPlayerInput>> {
  return z.object({
    name: z.string().nullish(),
    nationality: z.string().nullish(),
    seed: z.string().nullish(),
  });
}

export function ResultWidgetPositionInputSchema(): z.ZodObject<Properties<Types.ResultWidgetPositionInput>> {
  return z.object({
    gamePoints: z.string().nullish(),
    isServing: z.boolean().nullish(),
    isWinner: z.boolean().nullish(),
    players: z.array(z.lazy(() => ResultWidgetPlayerInputSchema().nullable())).nullish(),
    score: z.array(z.lazy(() => ResultWidgetScoreInputSchema().nullable())).nullish(),
  });
}

export function ResultWidgetScoreInputSchema(): z.ZodObject<Properties<Types.ResultWidgetScoreInput>> {
  return z.object({
    isWinner: z.boolean().nullish(),
    set: z.string().nullish(),
    tie: z.string().nullish(),
  });
}

export function ResultsCategoriesWidgetAdvInputSchema(): z.ZodObject<
  Properties<Types.ResultsCategoriesWidgetAdvInput>
> {
  return z.object({
    image: z.string().nullish(),
    link: z.string().nullish(),
  });
}

export function ResultsCategoriesWidgetInfoInputSchema(): z.ZodObject<
  Properties<Types.ResultsCategoriesWidgetInfoInput>
> {
  return z.object({
    dates: z.array(z.string().nullable()).nullish(),
    name: z.string().nullish(),
  });
}

export function ResultsWidgetMatchInputSchema(): z.ZodObject<Properties<Types.ResultsWidgetMatchInput>> {
  return z.object({
    firstPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    resultType: z.string().nullish(),
    roundName: z.string().nullish(),
    secondPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function UpdateCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateCalendarWidgetInput>> {
  return z.object({
    circuitCalendarLink: z.string().nullish(),
    filterList: z.array(z.string().nullable()).nullish(),
    tourId: z.string(),
    tournamentFilter: z.string(),
    tournaments: z.array(z.lazy(() => CalendarWidgetInfoInputSchema().nullable())).nullish(),
  });
}

export function UpdateCompetitionInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    competitionJson: z.any().nullish(),
    competitionName: z.string().nullish(),
    editorialDataId: z.string().nullish(),
    endDate: z.string().nullish(),
    startDate: z.string().nullish(),
    tourCalendarId: z.string(),
    tourId: z.string().nullish(),
    type: z.string().nullish(),
  });
}

export function UpdateEditorialDataInputSchema(): z.ZodObject<Properties<Types.UpdateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any().nullish(),
    seasonId: z.string(),
  });
}

export function UpdateEditorialDataV2InputSchema(): z.ZodObject<Properties<Types.UpdateEditorialDataV2Input>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any().nullish(),
    tourCalendarId: z.string(),
  });
}

export function UpdateLiveScoreWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateLiveScoreWidgetInput>> {
  return z.object({
    ctaUrls: z.lazy(() => LiveScoreWidgetCtaUrlInputSchema().nullish()),
    id: z.string(),
    liveTournaments: z.array(z.lazy(() => LiveScoreWidgetTournamentInputSchema().nullable())).nullish(),
  });
}

export function UpdateMatchCardWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateMatchCardWidgetInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    competitionId: z.string().nullish(),
    date: z.string().nullish(),
    firstPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    matchId: z.string(),
    resultType: z.string().nullish(),
    roundName: z.string().nullish(),
    secondPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
    tournamentId: z.string().nullish(),
  });
}

export function UpdateMatchInputSchema(): z.ZodObject<Properties<Types.UpdateMatchInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    date: z.number().nullish(),
    matchId: z.string(),
    matchLiveJson: z.any().nullish(),
    matchScheduleJson: z.any().nullish(),
    playerA1Id: z.string().nullish(),
    playerA2Id: z.string().nullish(),
    playerAseed: z.string().nullish(),
    playerB1Id: z.string().nullish(),
    playerB2Id: z.string().nullish(),
    playerBseed: z.string().nullish(),
    roundName: z.string().nullish(),
    status: z.string().nullish(),
    teamAId: z.string().nullish(),
    teamBId: z.string().nullish(),
    tourCalendarId: z.string().nullish(),
    tourId: z.string().nullish(),
    tournamentId: z.string(),
  });
}

export function UpdatePlayerInputSchema(): z.ZodObject<Properties<Types.UpdatePlayerInput>> {
  return z.object({
    nationalityId: z.string().nullish(),
    playerId: z.string(),
    playerJson: z.any().nullish(),
    playerName: z.string().nullish(),
  });
}

export function UpdateRankingInputSchema(): z.ZodObject<Properties<Types.UpdateRankingInput>> {
  return z.object({
    playerId: z.string().nullish(),
    rank: z.number(),
    rankingJson: z.any().nullish(),
    tourId: z.string(),
    type: z.string(),
  });
}

export function UpdateRankingWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateRankingWidgetInput>> {
  return z.object({
    rankings: z.array(z.lazy(() => RankingWidgetInfoInputSchema().nullable())).nullish(),
    tourId: z.string(),
    type: z.string(),
  });
}

export function UpdateResultsCategoriesWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateResultsCategoriesWidgetInput>
> {
  return z.object({
    adv: z.lazy(() => ResultsCategoriesWidgetAdvInputSchema().nullish()),
    categories: z.array(z.lazy(() => ResultsCategoriesWidgetInfoInputSchema().nullable())).nullish(),
    competitionId: z.string(),
    type: z.string(),
  });
}

export function UpdateResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateResultsContainerWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    ssi: z.array(z.string().nullable()).nullish(),
    type: z.string(),
  });
}

export function UpdateResultsWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateResultsWidgetInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    date: z.string().nullish(),
    day: z.string(),
    matches: z.array(z.lazy(() => ResultsWidgetMatchInputSchema().nullable())).nullish(),
    tournamentId: z.string(),
  });
}

export function UpdateTourCalendarInputSchema(): z.ZodObject<Properties<Types.UpdateTourCalendarInput>> {
  return z.object({
    active: z.boolean().nullish(),
    endDate: z.string().nullish(),
    startDate: z.string().nullish(),
    tourCalendarId: z.string(),
    tourId: z.string(),
    tourName: z.string().nullish(),
    tourYear: z.string().nullish(),
  });
}

export function UpdateTournamentDataWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateTournamentDataWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    data: z.any().nullish(),
    messageType: z.string().nullish(),
    type: z.string(),
  });
}

export function UpdateTournamentInputSchema(): z.ZodObject<Properties<Types.UpdateTournamentInput>> {
  return z.object({
    competitionId: z.string(),
    tournamentId: z.string(),
    tournamentJson: z.any().nullish(),
    tournamentName: z.string().nullish(),
  });
}
